import React, { useEffect, useState } from 'react';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { z } from 'zod';
import { useAddress } from '../../../../hooks/useAddress';
import { useAuth } from '../../../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../../constants/routes';
import { usePincodeLookup } from '../../../../hooks/usePincodeLookup';
import toast from 'react-hot-toast';

/* ---------------- ZOD SCHEMA ---------------- */
const addAddressSchema = z.object({
  nickname: z.string().min(1, 'Address nickname is required'),
  address1: z.string().min(1, 'Address is required').max(60, 'Address cannot exceed 60 characters'),
  address2: z.string().max(60, 'Address line 2 cannot exceed 60 characters').optional().or(z.literal('')),
  landmark: z.string().min(1, 'Landmark is required').max(60, 'Landmark cannot exceed 60 characters'),
  pincode: z.string().regex(/^\d{6}$/, 'Invalid pincode'),
  city: z.string().min(2, 'City is required'),
  state: z.string().min(2, 'State is required'),
  contactName: z.string().min(2, 'Contact name is required'),
  contactNumber: z.string().regex(/^[6-9]\d{9}$/, 'Invalid contact number'),
  altContactNumber: z
    .string()
    .regex(/^[6-9]\d{9}$/, 'Alt contact must be 10 digits starting with 6-9')
    .optional()
    .or(z.literal('')),
  email: z.string().email('Invalid email address').optional().or(z.literal('')),
  isDefault: z.boolean(),
});

type FormState = z.infer<typeof addAddressSchema>;

type AddWarehousePageProps = {
  onSuccess?: () => void;
  initialData?: any;
};

const AddWarehousePage: React.FC<AddWarehousePageProps> = ({
  onSuccess,
  initialData,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { saveAddress, updateAddress, isLoading } = useAddress(
    user?.id as string
  );

  const [form, setForm] = useState<FormState>({
    nickname: initialData?.company_name || '',
    address1: initialData?.address1 || '',
    address2: initialData?.address2 || '',
    landmark: initialData?.landmark || '',
    pincode: String(initialData?.pincode || ''),
    city: initialData?.city || '',
    state: initialData?.state || '',
    contactName: initialData?.contact_name || '',
    contactNumber: initialData?.contact_no || '',
    altContactNumber: initialData?.alt_contact_no || '',
    email: initialData?.email || '',
    isDefault: initialData?.default_status === 1,
  });

  const isEditing = !!initialData?.pickup_id;

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [cityStateEditable, setCityStateEditable] = useState(false);
  const { mutate: lookupPincode } = usePincodeLookup();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    if (['address1', 'address2', 'landmark'].includes(name) && value.length > 60) {
      toast.error(`${name === 'address1' ? 'Address' : name === 'address2' ? 'Address line 2' : 'Landmark'} cannot exceed 60 characters`);
      return;
    }
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  useEffect(() => {
    if (form.pincode.length !== 6) return;

    const timer = setTimeout(() => {
      lookupPincode(form.pincode, {
        onSuccess: (data) => {
          if (data) {
            setCityStateEditable(false);
            setForm((prev) => ({
              ...prev,
              city: data.District || prev.city,
              state: data.State || prev.state,
            }));
          } else {
            setCityStateEditable(true);
          }
        },
        onError: () => {
          setCityStateEditable(true);
        },
      });
    }, 600);

    return () => clearTimeout(timer); // cancel previous timer if user keeps typing
  }, [form.pincode, lookupPincode]);
  const handleSubmit = async () => {
    const result = addAddressSchema.safeParse(form);

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});

    const payload = {
      user_id: user?.id as string,
      company_name: form.nickname,
      address1: form.address1,
      address2: form.address2,
      city: form.city,
      state: form.state,
      pincode: form.pincode,
      landmark: form.landmark,
      country: 'IN',
      contact_name: form.contactName,
      contact_no: form.contactNumber,
      alt_contact_no: form.altContactNumber || null,
      email: form.email,
      default_status: form.isDefault ? 1 : 0,
      status: initialData?.status ?? 1,
    };

    if (isEditing) {
      await (updateAddress as any)({
        pickupId: initialData.pickup_id,
        payload,
      });
    } else {
      await saveAddress(payload as any);
    }

    if (onSuccess) {
      onSuccess();
    } else {
      navigate(ROUTES.SETTINGS_WAREHOUSE);
    }
  };

  return (
    <div className="py-6 lg:py-10">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {isEditing ? 'Edit Warehouse' : 'Manage Warehouse'}
        </h1>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>
            {user?.firstName}, {user?.email}
          </span>
          <Button variant="link" className="text-primary p-0 h-auto">
            Go to profile
          </Button>
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* LEFT */}
        <div className="flex-1 space-y-6">
          {/* Address Details */}
          <div className="bg-card border rounded-2xl p-6">
            <h3 className="font-bold text-2xl text-gray-900 mb-4">
              Address Details
            </h3>

            <label className="font-semibold text-base text-gray-700 mb-1 block">
              Address Nickname<span className="text-red-500">*</span>
            </label>
            <Input
              name="nickname"
              value={form.nickname}
              onChange={handleChange}
              placeholder="Select or search warehouse by name"
            />
            {errors.nickname && (
              <p className="text-xs text-red-500 mt-1">{errors.nickname}</p>
            )}
          </div>

          {/* Address */}
          <div className="bg-card border rounded-2xl p-6 space-y-4">
            <div>
              <h3 className="font-bold text-2xl text-gray-900">Address</h3>
              <p className="text-sm text-text3 mt-1">
                Enter the Delivery Details of your buyer for whom you are
                shipping this order
              </p>
            </div>

            <div>
              <label className="text-base font-semibold text-text mb-1 block">
                 Address<span className="text-red-500">*</span>
              </label>
              <Input
                name="address1"
                value={form.address1}
                onChange={handleChange}
                maxLength={60}
                placeholder="House Number & Bhart Park Mira Bhayander Road"
              />
              {errors.address1 && (
                <p className="text-xs text-red-500 mt-1">{errors.address1}</p>
              )}
            </div>
            <div>
              <label className="text-base font-semibold text-text mb-1 block">
                 Address line 2
              </label>
              <Input
                name="address2"
                value={form.address2}
                onChange={handleChange}
                maxLength={60}
                placeholder="House Number & Bhart Park Mira Bhayander Road"
              />
              {errors.address2 && (
                <p className="text-xs text-red-500 mt-1">{errors.address2}</p>
              )}
            </div>

            <div>
              <label className="text-base font-semibold text-gray-700 mb-1 block">
                Landmark<span className="text-red-500">*</span>
              </label>
              <Input
                name="landmark"
                value={form.landmark}
                onChange={handleChange}
                maxLength={60}
                placeholder="Full address (including housenumber, street)"
              />
              {errors.landmark && (
                <p className="text-xs text-red-500 mt-1">{errors.landmark}</p>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <label className="text-base font-semibold text-text mb-1 block">
                  Pin-code<span className="text-red-500">*</span>
                </label>
                <Input
                  name="pincode"
                  value={form.pincode}
                  onChange={handleChange}
                  placeholder="Enter Pin-code"
                />
                {errors.pincode && (
                  <p className="text-xs text-red-500 mt-1">{errors.pincode}</p>
                )}
              </div>

              <div>
                <label className="text-base font-semibold text-text mb-1 block">
                  City
                </label>
                <Input
                  name="city"
                  className={cityStateEditable ? '' : 'cursor-not-allowed'}
                  disabled={!cityStateEditable}
                  value={form.city}
                  onChange={handleChange}
                  placeholder="City"
                />
              </div>

              <div>
                <label className="text-base font-semibold text-text mb-1 block">
                  State
                </label>
                <Input
                  name="state"
                  className={cityStateEditable ? '' : 'cursor-not-allowed'}
                  disabled={!cityStateEditable}
                  value={form.state}
                  onChange={handleChange}
                  placeholder="State"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500">
              <input
                type="checkbox"
                name="isDefault"
                checked={form.isDefault}
                onChange={handleChange}
                className="rounded"
              />
              <span>Make this Default Address</span>
            </div>
          </div>

          {/* Contact Details */}
          <div className="bg-card border rounded-2xl p-6">
            <h3 className="text-2xl font-bold text-text mb-4">
              Contact Details
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              <div>
                <label className="text-base font-semibold text-text mb-1 block">
                  Name<span className="text-red-500">*</span>
                </label>
                <Input
                  name="contactName"
                  value={form.contactName}
                  onChange={handleChange}
                  placeholder="Name"
                />
              </div>

              <div>
                <label className="text-base font-semibold text-text mb-1 block">
                  Contact Number<span className="text-red-500">*</span>
                </label>
                <Input
                  name="contactNumber"
                  value={form.contactNumber}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    if (value.length === 1 && /^[0-5]/.test(value)) return;
                    setForm((prev) => ({ ...prev, contactNumber: value }));
                  }}
                  placeholder="9909099121"
                />
                {errors.contactNumber && (
                  <p className="text-xs text-red-500 mt-1">{errors.contactNumber}</p>
                )}
              </div>

              <div>
                <label className="text-base font-semibold text-text mb-1 block">
                  Email Address
                </label>
                <Input
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="asbdak@gmail.com"
                />
              </div>

              <div>
                <label className="text-base font-semibold text-text mb-1 block">
                  Alt. Contact Number
                </label>
                <Input
                  name="altContactNumber"
                  value={form.altContactNumber}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    if (value.length === 1 && /^[0-5]/.test(value)) return;
                    setForm((prev) => ({ ...prev, altContactNumber: value }));
                  }}
                  placeholder="Optional"
                />
                {errors.altContactNumber && (
                  <p className="text-xs text-red-500 mt-1">{errors.altContactNumber}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-center mt-4">
        <Button
          variant="default"
          onClick={handleSubmit}
          disabled={isLoading}
          className="w-fit text-lg"
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export default AddWarehousePage;
